import { Alert } from '@evaneos/b2b-ui/legacy';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { Avatar } from '../../../resources/agent/components/Avatar';
import { AlertInTripReviewsInformation } from '../../../resources/in-trip-review/types';
import { AlertPreTripReviewsInformation } from '../../../resources/pre-trip-review/types';
import { useTrackEventAmplitude } from '../../../shared/analytics';

import s from '../Todo.css';

type Props = {
    preTripReviews: AlertPreTripReviewsInformation[];
    inTripReviews: AlertInTripReviewsInformation[];
    isNotFilteredByConnectedAgent: boolean;
};

export const ReviewsAlert = ({
    preTripReviews,
    inTripReviews,
    isNotFilteredByConnectedAgent,
}: Props): JSX.Element => {
    const { formatMessage } = useIntl();

    const displayPreTripReviews = preTripReviews.length > 0;
    const displayInTripReviews = inTripReviews.length > 0;

    return (
        <Alert
            collapsible={true}
            className={s.reviewsAlert}
            type="warning"
            showContentByDefault={false}
            title={
                formatMessage(
                    {
                        id: 'dossier.trip_reviews.title',
                    },
                    {
                        total: preTripReviews.length + inTripReviews.length,
                        end: () => (
                            <span className={s.inTripLength}>
                                {formatMessage(
                                    {
                                        id: 'dossier.trip_reviews.subtitle',
                                    },
                                    {
                                        inTrip: inTripReviews.length,
                                    },
                                )}
                            </span>
                        ),
                    },
                ) as unknown as string
            }
            data-testid="todo-reviews-alert"
        >
            {displayInTripReviews && (
                <InTripReviews
                    reviews={inTripReviews}
                    displayAvatar={isNotFilteredByConnectedAgent}
                />
            )}
            {displayPreTripReviews && (
                <PreTripReviews
                    reviews={preTripReviews}
                    displayAvatar={isNotFilteredByConnectedAgent}
                />
            )}
        </Alert>
    );
};

type InTripReviewsProps = {
    displayAvatar: boolean;
    reviews: AlertInTripReviewsInformation[];
};

function InTripReviews({ displayAvatar, reviews }: InTripReviewsProps) {
    const { formatMessage } = useIntl();
    return (
        <>
            <p className={s.reviewCategory}>
                {formatMessage({ id: 'dossier.trip_reviews.in_trip' }, { count: reviews.length })}
            </p>
            <RenderReviews reviews={reviews} displayAvatar={displayAvatar} type="In trip" />
        </>
    );
}

type PreTripReviewsProps = {
    displayAvatar: boolean;
    reviews: AlertPreTripReviewsInformation[];
};

function PreTripReviews({ displayAvatar, reviews }: PreTripReviewsProps) {
    const { formatMessage } = useIntl();
    return (
        <>
            <p className={s.reviewCategory}>
                {formatMessage({ id: 'dossier.trip_reviews.pre_trip' }, { count: reviews.length })}
            </p>
            <RenderReviews reviews={reviews} displayAvatar={displayAvatar} type="Pre booking" />
        </>
    );
}

function RenderReviews({
    displayAvatar,
    reviews,
    type,
}: (PreTripReviewsProps | InTripReviewsProps) & {
    type: 'Pre booking' | 'In trip';
}) {
    const { formatMessage } = useIntl();
    const { trackEvent } = useTrackEventAmplitude();

    return (
        <div className={s.preTripReviewBody}>
            {reviews.map((review) => (
                <div className={s.preTripReviewRow} key={review.dossierId}>
                    {displayAvatar && (
                        <Avatar
                            className={s.avatar}
                            name={review.agentName}
                            photoUrl={review.agentPhotoUrl}
                        />
                    )}
                    <span className="title-s">
                        {review.createdAt &&
                            review.createdAt.toLocaleDateString(undefined, {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                            })}
                    </span>
                    <p className={s.requesterName}>{review.requesterName}</p>
                    <p>
                        <Link
                            to={{
                                pathname: `/dossier/${review.dossierId}`,
                            }}
                            onClick={() =>
                                trackEvent((ampli, defaultProperties) =>
                                    ampli.viewFeedbackClicked({
                                        ...defaultProperties,
                                        feedback_type: type,
                                    }),
                                )
                            }
                        >
                            {formatMessage({
                                id: 'dossier.trip_reviews.see_review',
                            })}
                        </Link>
                    </p>
                </div>
            ))}
        </div>
    );
}
